import React from 'react';
import { ContentBanner, Badge } from '@modernhealth/vital-components';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { EventLexicon } from '@modernhealth/analytics-lexicon';
import styled from '@emotion/styled';
import { useAnalytics } from '@modernhealth/web-analytics';

import { InternalLinkButton } from 'src/shared/components/InternalLinkButton';
import { PathwaySessionState, PathwayProgressState } from 'src/generated/webapp_gql';
import { PathwaySessionDetailModalRouteToolkit } from '../pages/PathwaySessionDetailModalPage';
import type {
    PathwayTopicFragment,
    PathwayProviderFragment,
} from '../documents/fragments.generated';
import SessionCompleteImage from '../assets/session-complete-web.png';
import { colors } from '@modernhealth/vital-foundation';
import { useIntl } from 'react-intl';

interface Props {
    topic: PathwayTopicFragment;
    pathwayProvider: PathwayProviderFragment;
    pathwayProgress: PathwayProgressState;
}

const SessionStateToActionComponent: Record<
    PathwaySessionState,
    (props: Props) => JSX.Element | null
> = {
    [PathwaySessionState.Unscheduled]: ({ pathwayProvider, pathwayProgress }) => {
        const { t } = useTranslation();
        const analytics = useAnalytics();
        const { uuid } = useParams<{ uuid: string }>();

        return (
            <InternalLinkButton
                onClick={() =>
                    analytics.trackEvent(
                        EventLexicon.clickScheduleASession({
                            providerUuid: pathwayProvider.provider.uuid,
                            pathwayUuid: uuid,
                            pathwayProgress: pathwayProgress,
                        }),
                    )
                }
                to={{
                    pathname: '/schedule-session/calendar',
                    state: {
                        coachUuid: pathwayProvider.provider.uuid,
                    },
                }}
                rounded
            >
                {t('pathways:pathway_detail.action.book_session')}
            </InternalLinkButton>
        );
    },
    [PathwaySessionState.Scheduled]: ({ pathwayProvider, pathwayProgress }) => {
        const navigateToSessionDetailModal = PathwaySessionDetailModalRouteToolkit.useNavigate();
        const { t } = useTranslation();
        const analytics = useAnalytics();
        const { uuid } = useParams<{ uuid: string }>();

        return (
            <InternalLinkButton
                onClick={() =>
                    analytics.trackEvent(
                        EventLexicon.clickViewSession({
                            providerUuid: pathwayProvider.provider.uuid,
                            pathwayUuid: uuid,
                            pathwayProgress: pathwayProgress,
                        }),
                    )
                }
                to={navigateToSessionDetailModal.getLocationDescriptor()}
                variant="secondary"
                rounded
            >
                {t('pathways:pathway_detail.action.view_session_details')}
            </InternalLinkButton>
        );
    },
    [PathwaySessionState.Joinable]: ({ topic, pathwayProvider, pathwayProgress }) => {
        const { t } = useTranslation();
        const analytics = useAnalytics();
        const { uuid } = useParams<{ uuid: string }>();

        return (
            <InternalLinkButton
                onClick={() =>
                    analytics.trackEvent(
                        EventLexicon.clickJoinSession({
                            visitUuid: topic.sessionDetail?.coachingVisit?.uuid,
                            providerUuid: pathwayProvider.provider.uuid,
                            creationSource: 'PATHWAYS',
                            pathwayUuid: uuid,
                            pathwayProgress: pathwayProgress,
                        }),
                    )
                }
                to={`/video-session/${topic.sessionDetail?.coachingVisit?.uuid}`}
                rounded
            >
                {t('pathways:pathway_detail.action.join_session')}
            </InternalLinkButton>
        );
    },
    [PathwaySessionState.Completed]: () => {
        const { t } = useTranslation();

        return (
            <Badge
                title={`🎉 ${t('pathways:pathway_detail.session_complete')}`}
                variant="success"
                size="medium"
            />
        );
    },
    [PathwaySessionState.Locked]: ({ pathwayProvider }) => {
        const { t } = useTranslation();

        return (
            <InternalLinkButton
                to={{
                    pathname: '/schedule-session',
                    search: `providerUuid=${pathwayProvider.provider.uuid}`,
                }}
                rounded
                disabled
            >
                {t('pathways:pathway_detail.action.book_session')}
            </InternalLinkButton>
        );
    },
};

/**
 * Exported for testing purposes only - not for general use outside of SessionDetailBanner.
 */
export const DescriptionTranslationKeyBySessionState = {
    [PathwaySessionState.Scheduled]: 'pathways:pathway_detail.session_description.scheduled',
    [PathwaySessionState.Joinable]: 'pathways:pathway_detail.session_description.joinable',
    [PathwaySessionState.Completed]: 'pathways:pathway_detail.session_description.completed',
} as const;

/**
 * Exported for testing purposes only - not for general use outside of SessionDetailBanner.
 */
export const useFormatPathwaySessionDetailBannerDateTime = (date: Date) => {
    const intl = useIntl();
    return intl.formatTime(date, {
        day: '2-digit',
        month: 'long',
        weekday: 'long',
        hour: 'numeric',
        minute: 'numeric',
        timeZoneName: 'short',
    });
};

const useSessionDetailBannerDescription = (
    sessionState: PathwaySessionState,
    topic: PathwayTopicFragment,
    pathwayProvider: PathwayProviderFragment,
) => {
    const { t } = useTranslation();
    const formattedSessionDateTime = useFormatPathwaySessionDetailBannerDateTime(
        topic.sessionDetail?.coachingVisit?.scheduledTime ?? '',
    );

    if (
        sessionState === PathwaySessionState.Unscheduled ||
        sessionState === PathwaySessionState.Locked
    ) {
        return topic.description;
    }

    if (
        sessionState === PathwaySessionState.Scheduled ||
        sessionState === PathwaySessionState.Joinable ||
        sessionState === PathwaySessionState.Completed
    ) {
        return t(DescriptionTranslationKeyBySessionState[sessionState], {
            providerFirstName: pathwayProvider.provider.firstName,
            dayAndTimeWithTimezone: formattedSessionDateTime,
        });
    }
    return null;
};

export const SessionDetailBanner = ({ topic, pathwayProvider, pathwayProgress }: Props) => {
    const sessionState = topic.sessionDetail?.state ?? PathwaySessionState.Unscheduled;
    const ActionComponent = SessionStateToActionComponent[sessionState];
    const description = useSessionDetailBannerDescription(sessionState, topic, pathwayProvider);
    const sessionComplete = sessionState === PathwaySessionState.Completed;
    const bannerImageSrc = sessionComplete ? SessionCompleteImage : pathwayProvider.provider.avatar;

    return (
        <ContentBanner
            title={topic.title}
            subtitle={topic.subtitle}
            description={description ?? ''}
            backgroundColor={
                sessionState === PathwaySessionState.Completed ? '#EDFFEF' : colors.violet05
            }
            theme="dark"
            footer={
                <StyledFooterContainer>
                    {bannerImageSrc ? (
                        // Pure hack, the ContentBanner doesn't fit the image
                        // properly using the image prop.
                        <StyledImage src={bannerImageSrc} />
                    ) : undefined}
                    {ActionComponent && (
                        <ActionComponent
                            topic={topic}
                            pathwayProvider={pathwayProvider}
                            pathwayProgress={pathwayProgress}
                        />
                    )}
                </StyledFooterContainer>
            }
        />
    );
};

const StyledFooterContainer = styled.div({
    marginTop: 30,
});

const StyledImage = styled.img({
    position: 'absolute',
    top: 0,
    right: 0,
    width: '35%',
    height: '100%',
    objectFit: 'cover',
});
